import React, { useState } from 'react';
import { 
  Box, Container, Typography, TextField, Grid, Radio, RadioGroup, FormControlLabel, 
  Button, Paper, Alert
} from '@mui/material';
import { useSelector } from 'react-redux';
import axios from 'axios';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';


import axiosServices from '../../api/interceptor';

const ProbationEvaluation = () => {
  const { user } = useSelector((state) => state.tokens);

  const [formData, setFormData] = useState({
    employeeName: '',
    positionRole: '',
    departmentTeam: '',
    startDate: '',
    probationEndDate: '',
    dateOfEvaluation: '',
    evaluatorNameTitle: user.fullName || '',
    ratings: {
      qualityOfWork: { score: '', comments: '' },
      productivityAndDeadlines: { score: '', comments: '' },
      skillsAndJobKnowledge: { score: '', comments: '' },
      learningAndAdaptability: { score: '', comments: '' },
      communicationAndTeamwork: { score: '', comments: '' },
      reliabilityAndInitiative: { score: '', comments: '' }
    },
    overallAssessment: {
      keyStrengths: '',
      areasForImprovement: ''
    },
    probationRecommendation: {
      recommendation: '',
      justification: ''
    },
    signOff: {
      evaluatorSignature: '',
      date: ''
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRatingChange = (category, field, value) => {
    setFormData(prev => ({
      ...prev,
      ratings: {
        ...prev.ratings,
        [category]: {
          ...prev.ratings[category],
          [field]: value
        }
      }
    }));
  };

  const handleOverallChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      overallAssessment: {
        ...prev.overallAssessment,
        [field]: value
      }
    }));
  };

  const handleRecommendationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      probationRecommendation: {
        ...prev.probationRecommendation,
        [field]: value
      }
    }));
  };

  const handleSignOffChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      signOff: {
        ...prev.signOff,
        [field]: value
      }
    }));
  };

  const generatePDF = async () => {
    const input = document.getElementById('evaluation-form-content');
    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;
    
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = position - pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    return pdf;
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setSuccessMsg('');
      
      const pdf = await generatePDF();
      const pdfBase64 = pdf.output('datauristring'); // Get full base64 string
      
      const payload = {
        ...formData,
        createdBy: user._id || user.id,
        pdfAttachment: pdfBase64
      };
      await axiosServices.post('/evaluations', payload);
      setSuccessMsg('Evaluation submitted successfully!');
    } catch (error) {
      console.error(error);
      alert('Error submitting evaluation: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const exportPDF = async () => {
    const pdf = await generatePDF();
    pdf.save(`Probation_Evaluation_${formData.employeeName || 'Employee'}.pdf`);
  };

  const renderRatingRow = (title, description, categoryKey, index) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="subtitle1" fontWeight="bold">{index}. {title}</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>{description}</Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <RadioGroup 
            row 
            value={formData.ratings[categoryKey].score} 
            onChange={(e) => handleRatingChange(categoryKey, 'score', e.target.value)}
          >
            {[1, 2, 3, 4, 5].map(val => (
              <FormControlLabel key={val} value={val} control={<Radio size="small" />} label={val.toString()} sx={{ mr: 3 }} />
            ))}
          </RadioGroup>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField 
            fullWidth 
            size="small" 
            variant="outlined" 
            placeholder="Comments:" 
            value={formData.ratings[categoryKey].comments}
            onChange={(e) => handleRatingChange(categoryKey, 'comments', e.target.value)}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const isLeader = Boolean(user?.isTeamLeader || user?.is_leader || user?.isLeader);
  const isAdminOrHR = user?.permission === "Admin" || user?.permission === "Admin-a" || user?.permission === "HR Management";

  if (!user || (!isLeader && !isAdminOrHR)) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>You are not authorized to view this page. Only Team Leaders, HR Management and Admins can access the Probation Evaluation form.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
        <Button variant="outlined" onClick={exportPDF}>Save / print as PDF</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>Submit evaluation</Button>
      </Box>

      <Paper id="evaluation-form-content" elevation={3} sx={{ p: 0, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ bgcolor: '#203864', color: 'white', p: 4 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h4" fontWeight="bold">Probation Period Evaluation Form</Typography>
              <Typography variant="subtitle1">Confidential - to be completed by the direct supervisor / team lead</Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2">Page 1 of 2</Typography>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ p: 4 }}>
          {/* Employee Information */}
          <Box sx={{ bgcolor: '#eef2f6', p: 1, mb: 2 }}>
            <Typography variant="h6" color="#203864" fontWeight="bold">Employee Information</Typography>
          </Box>
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">Employee name</Typography>
              <TextField fullWidth size="small" variant="standard" name="employeeName" value={formData.employeeName} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">Position / role</Typography>
              <TextField fullWidth size="small" variant="standard" name="positionRole" value={formData.positionRole} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">Department / team</Typography>
              <TextField fullWidth size="small" variant="standard" name="departmentTeam" value={formData.departmentTeam} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">Start date</Typography>
              <TextField fullWidth size="small" variant="standard" type="date" InputLabelProps={{ shrink: true }} name="startDate" value={formData.startDate} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">Probation end date</Typography>
              <TextField fullWidth size="small" variant="standard" type="date" InputLabelProps={{ shrink: true }} name="probationEndDate" value={formData.probationEndDate} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">Date of evaluation</Typography>
              <TextField fullWidth size="small" variant="standard" type="date" InputLabelProps={{ shrink: true }} name="dateOfEvaluation" value={formData.dateOfEvaluation} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2">Evaluator name & title</Typography>
              <TextField fullWidth size="small" variant="standard" name="evaluatorNameTitle" value={formData.evaluatorNameTitle} onChange={handleInputChange} />
            </Grid>
          </Grid>

          {/* Performance Ratings */}
          <Box sx={{ bgcolor: '#eef2f6', p: 1, mb: 1 }}>
            <Typography variant="h6" color="#203864" fontWeight="bold">Performance Ratings</Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Rate each area: 1 = Poor  2 = Below expectations  3 = Meets expectations  4 = Exceeds expectations  5 = Excellent
          </Typography>

          {renderRatingRow('Quality of work', 'Accuracy, attention to detail, adherence to content standards and brand guidelines.', 'qualityOfWork', 1)}
          {renderRatingRow('Productivity & deadlines', 'Volume of output, ability to deliver on time, workload management.', 'productivityAndDeadlines', 2)}
          {renderRatingRow('Skills & job knowledge', 'Writing/editing skills, command of tools and platforms, understanding of the role.', 'skillsAndJobKnowledge', 3)}
          {renderRatingRow('Learning & adaptability', 'Responds to feedback, learns processes quickly, improves over time.', 'learningAndAdaptability', 4)}
          {renderRatingRow('Communication & teamwork', 'Collaboration with the team, responsiveness, clarity in communication.', 'communicationAndTeamwork', 5)}
          {renderRatingRow('Reliability & initiative', 'Attendance, punctuality, ownership of tasks, proactivity.', 'reliabilityAndInitiative', 6)}

          {/* Overall Assessment */}
          <Box sx={{ bgcolor: '#eef2f6', p: 1, mb: 2, mt: 4 }}>
            <Typography variant="h6" color="#203864" fontWeight="bold">Overall Assessment</Typography>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>Key strengths</Typography>
            <TextField fullWidth multiline rows={3} variant="outlined" value={formData.overallAssessment.keyStrengths} onChange={(e) => handleOverallChange('keyStrengths', e.target.value)} />
          </Box>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>Areas for improvement</Typography>
            <TextField fullWidth multiline rows={3} variant="outlined" value={formData.overallAssessment.areasForImprovement} onChange={(e) => handleOverallChange('areasForImprovement', e.target.value)} />
          </Box>

          {/* Probation Recommendation */}
          <Box sx={{ bgcolor: '#eef2f6', p: 1, mb: 2 }}>
            <Typography variant="h6" color="#203864" fontWeight="bold">Probation Recommendation</Typography>
          </Box>
          <RadioGroup 
            value={formData.probationRecommendation.recommendation}
            onChange={(e) => handleRecommendationChange('recommendation', e.target.value)}
            sx={{ mb: 3 }}
          >
            <FormControlLabel value="Confirm employment (pass probation)" control={<Radio />} label="Confirm employment (pass probation)" />
            <FormControlLabel value="Extend probation period" control={<Radio />} label="Extend probation period" />
            <FormControlLabel value="Do not continue employment" control={<Radio />} label="Do not continue employment" />
          </RadioGroup>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>Justification for recommendation</Typography>
            <TextField fullWidth multiline rows={3} variant="outlined" value={formData.probationRecommendation.justification} onChange={(e) => handleRecommendationChange('justification', e.target.value)} />
          </Box>

          {/* Sign-off */}
          <Box sx={{ bgcolor: '#eef2f6', p: 1, mb: 3 }}>
            <Typography variant="h6" color="#203864" fontWeight="bold">Sign-off</Typography>
          </Box>
          <Grid container spacing={4} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">Evaluator signature</Typography>
              <TextField fullWidth size="small" variant="standard" value={formData.signOff.evaluatorSignature} onChange={(e) => handleSignOffChange('evaluatorSignature', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">Date</Typography>
              <TextField fullWidth size="small" variant="standard" type="date" InputLabelProps={{ shrink: true }} value={formData.signOff.date} onChange={(e) => handleSignOffChange('date', e.target.value)} />
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default ProbationEvaluation;
